import { Children } from "react";
import { FormGroup } from "../../../components/form";
import * as Yup from "yup";
import { FormArrayConfig } from "../../enums/FormArrayConfig";

const saveprompt = "Remember to save frequently to avoid losing progress";

export const contextOfChildForm: FormGroup = {
  type: "formGroup",
  fields: [
    {
      type: "formGroup",
      key: "childsDiagnosisDetails",
      label: "Child's disability or diagnosis",
      warn: "If the child has more than one, select all that apply",
      hideExpression: (context) => context.applicationCategory == 0, // HIDE *IF* [Application Category] component, [Application Category] field = [Complex Medical Needs]
      test: [
        "chooseOne",
        "A response is required",
        (value: any) => {
          return (
            value?.autism ||
            value?.vision ||
            value?.cerebralPalsy ||
            value?.devDelay ||
            value?.downSyndrome ||
            value?.genetic ||
            value?.hearing ||
            value?.epilepsy ||
            value?.assessment ||
            value?.other
          );
        },
      ],
      props: {
        saveprompt: saveprompt,
        isRequired: true,
      },
      fields: [
        {
          type: "field",
          key: "advisory",
          fieldType: "advisory",
          props: {
            type: "fieldadv",
            Body: () => <div>If the child has more than one, select all that apply</div>,
          },
        },
        {
          type: "field",
          key: "autism",
          fieldType: "checkbox",
          label: "Autism Spectrum Disorder (ASD)",
        },
        {
          type: "field",
          fieldType: "radio",
          key: "autismLevel",
          hideExpression: (context, values) => !values.childsDiagnosisDetails.autism,
          props: {
            className: "ml-10 cursor-pointer radio-btn",
            options: [
              {
                label: "Level 1",
                value: 1,
              },
              {
                label: "Level 2",
                value: 2,
              },
              {
                label: "Level 3",
                value: 3,
              },
            ],
          },
        },
        {
          type: "field",
          key: "vision",
          fieldType: "checkbox",
          label: "Blind / low vision",
        },
        {
          type: "field",
          key: "cerebralPalsy",
          fieldType: "checkbox",
          label: "Cerebral palsy",
        },
        {
          type: "field",
          key: "devDelay",
          fieldType: "checkbox",
          label: "Developmental delay/global developmental delay",
          warn: "Children with a NDIS plan but no diagnosis fit into this category",
        },
        {
          type: "field",
          key: "downSyndrome",
          fieldType: "checkbox",
          label: "Down Syndrome (trisomy 21)",
        },
        {
          type: "field",
          key: "genetic",
          fieldType: "checkbox",
          label: "Other chromosomal/genetic abnormalities",
          warn: "Examples include DiGeorge syndrome, Edwards syndrome (trisomy 18), Fragile X syndrome and various chromosomal deletions / duplications / repetitions.",
        },
        {
          type: "field",
          fieldType: "input",
          key: "geneticDetails",
          label: "Please note the abnormality identified, if known",
          hideExpression: (context, values) => !values.childsDiagnosisDetails.genetic,
          validation: Yup.string().trim().required("A response is required").max(250, "No more than 250 characters may be entered in this field"),
          expressionProps: {
            warn: (values, model) => (
              <div className="flex justify-between w-full">
                <p className="mr-2">
                  This should be no longer than one sentence. Please don't include information about the functional impacts of the disability /
                  diagnosis on the child
                </p>
                <p className={model.childsDiagnosisDetails.geneticDetails?.length > 250 ? "error character-count" : "character-count"}>
                  {model.childsDiagnosisDetails.geneticDetails?.length ?? 0} / 250
                </p>
              </div>
            ),
          },
        },
        {
          type: "field",
          key: "hearing",
          fieldType: "checkbox",
          label: "Deaf / hard of hearing",
        },
        {
          type: "field",
          key: "epilepsy",
          fieldType: "checkbox",
          label: "Epilepsy",
        },
        {
          type: "field",
          key: "assessment",
          fieldType: "checkbox",
          label: "Undergoing continuing assessment for disability",
        },
        {
          type: "field",
          fieldType: "input",
          key: "assessmentDetails",
          label: "Please note the disability / diagnosis being investigated",
          hideExpression: (context, values) => !values.childsDiagnosisDetails.assessment,
          validation: Yup.string().trim().required("A response is required").max(250, "No more than 250 characters may be entered in this field"),
          expressionProps: {
            warn: (values, model) => (
              <div className="flex justify-between w-full">
                <p className="mr-2">
                  This should be no longer than one sentence. Please don't include information about the functional impacts of the
                  disability/diagnosis on the child
                </p>
                <p className={model.childsDiagnosisDetails.assessmentDetails?.length > 250 ? "error character-count" : "character-count"}>
                  {model.childsDiagnosisDetails.assessmentDetails?.length ?? 0} / 250
                </p>
              </div>
            ),
          },
        },
        {
          type: "field",
          key: "other",
          fieldType: "checkbox",
          label: "Other",
        },
        {
          type: "field",
          fieldType: "input",
          key: "otherDetails",
          label: "Please Specify",
          hideExpression: (context, values) => !values.childsDiagnosisDetails.other,
          validation: Yup.string().trim().required("A response is required").max(250, "No more than 250 characters may be entered in this field"),
          expressionProps: {
            warn: (values, model) => (
              <div className="flex justify-between w-full">
                <p className="mr-2">
                  This should be no longer than one sentence. Please don't include information about the functional impacts of the disability /
                  diagnosis on the child
                </p>
                <p className={model.childsDiagnosisDetails.otherDetails?.length > 250 ? "error character-count" : "character-count"}>
                  {model.childsDiagnosisDetails.otherDetails?.length ?? 0} / 250
                </p>
              </div>
            ),
          },
        },
      ],
    },
    {
      type: "field",
      fieldType: "textarea",
      key: "describeChildsInterests",
      label: "Describe the child's interests",
      extendedlabel: (
        <>
          <p>For example:</p>
          <ul>
            <li>Child enjoys X and engages for long periods on this activity and is very focussed on Y.</li>
            <li>Child loves X and will speak to educators about X for extended periods of time.</li>
            <li>
              Child's favourite activity is X, child will independently seek out other children to play X for as long as the children are all
              outdoors.
            </li>
          </ul>
        </>
      ),
      validation: Yup.string().trim().required("A response is required"),
      props: {
        saveprompt: saveprompt,
      },
    },
    {
      type: "field",
      fieldType: "textarea",
      key: "describeChildsStrengthsAbilities",
      label: "Describe the child's strengths and abilities",
      extendedlabel: (
        <>
          <p>You can find more information about strengths-based approach here.</p>
          <ul>
            <li>VEYLDF pracguideequityanddiversity2017.pdf</li>
            <li>
              <a
                target="_blank"
                title="Link opens in a new tab"
                href="https://www.education.vic.gov.au/documents/childhood/professionals/learning/strengthbappr.pdf"
                rel="noreferrer">
                Transition to school
              </a>
              <i aria-hidden="true" className="pi pi-external-link"></i>
            </li>
            <li>Examples are provided above</li>
          </ul>
        </>
      ),
      validation: Yup.string().trim().required("A response is required"),
      props: {
        saveprompt: saveprompt,
      },
    },
    {
      type: "formArray",
      key: "concerns",
      props: {
        removeLabel: "Remove Concern",
        addLabel: "Add Another Behaviour / Concern",
        isRequired: true,
      },
      config: FormArrayConfig.DeleteCustom,
      label: "Complete the table below to outline the behaviours exhibited by the child that are a significant risk of injury to self or others:",
      hideExpression: (context) => context.subcriteria === 0 || context.subcriteria === 2, //HIDE table *IF* [Application Category] component, [Disability sub-criteria] field = [N/A] *OR* [Restricted capacity for movement]
      fields: [
        {
          type: "formGroup",
          customWrapper: ({ children }: any) => (
            <table className="concerns-table">
              <tbody>
                {Children.toArray(children).reduce<JSX.Element[]>((rows, child, i, arr) => {
                  if (i % 2 === 0) {
                    rows.push(
                      <tr key={i} className="w-100">
                        <th>{child}</th>
                        {arr[i + 1] && <td>{arr[i + 1]}</td>}
                      </tr>
                    );
                  }
                  return rows;
                }, [])}
              </tbody>
            </table>
          ),
          fields: [
            {
              type: "pInput",
              key: "x",
              label: "Behaviour / Concern",
              props: {
                css: "ml-2",
              },
            },
            {
              type: "field",
              fieldType: "textarea",
              key: "behvaiourConcern",
              validation: Yup.string().trim().required("A response is required"),
              props: {
                saveprompt: saveprompt,
              },
            },
            {
              type: "pInput",
              key: "x",
              label: "Describe the behaviour and how this creates a significant risk of injury to self or others",
              props: {
                css: "ml-2",
              },
            },
            {
              type: "field",
              fieldType: "textarea",
              key: "describeBehaviourRisk",
              validation: Yup.string().trim().required("A response is required"),
              props: {
                saveprompt: saveprompt,
              },
            },
            {
              type: "pInput",
              key: "x",
              label: "When and where does the behaviour occur and are there any triggers?",
              props: {
                css: "ml-2",
              },
            },
            {
              type: "field",
              fieldType: "textarea",
              key: "whereDoesBehaviourOccur",
              validation: Yup.string().trim().required("A response is required"),
              props: {
                saveprompt: saveprompt,
              },
            },
            {
              type: "pInput",
              key: "x",
              label: "Frequency and duration of behaviour e.g. 3 times a session for up to 30 minutes",
              props: {
                css: "ml-2",
              },
            },
            {
              type: "formGroup",
              customWrapper: ({ children }: any) => (
                <div className="flex" style={{ maxWidth: "1250px" }}>
                  {Children.map(children, (child) => child)}
                </div>
              ),
              fields: [
                {
                  type: "field",
                  key: "timesPer",
                  fieldType: "number",
                  validation: Yup.number()
                    .required("A response is required")
                    .min(1, "Please enter a valid number")
                    .max(99, "Please enter a valid number"),
                },
                {
                  type: "pInput",
                  key: "x",
                  label: "time(s) per",
                  props: {
                    css: "mx-3 my-2 whitespace-nowrap",
                  },
                },
                {
                  type: "field",
                  fieldType: "dropdown",
                  key: "perMeasurement",
                  props: {
                    fieldclassname: "measurement-width",
                    options: [
                      {
                        label: "Hour",
                        value: 1,
                      },
                      {
                        label: "Day",
                        value: 2,
                      },
                      {
                        label: "Week",
                        value: 3,
                      },
                      {
                        label: "Month",
                        value: 4,
                      },
                      {
                        label: "Term",
                        value: 5,
                      },
                      {
                        label: "Year",
                        value: 6,
                      },
                    ],
                  },
                  validation: Yup.string().trim().required("A response is required"),
                },
                {
                  type: "pInput",
                  key: "x",
                  label: "for up to",
                  props: {
                    css: "mx-3 my-2 whitespace-nowrap",
                  },
                },
                {
                  type: "field",
                  key: "minutes",
                  fieldType: "number",
                  validation: Yup.number()
                    .required("A response is required")
                    .min(1, "Please enter a valid number")
                    .max(999, "Please enter a valid number"),
                },
                {
                  type: "pInput",
                  key: "x",
                  label: "minutes",
                  props: {
                    css: "ml-3 my-2 whitespace-nowrap",
                  },
                },
              ],
            },
            {
              type: "pInput",
              key: "x",
              label: "Strategies used to guide the child's behaviour",
              props: {
                css: "ml-2",
              },
            },
            {
              type: "field",
              fieldType: "textarea",
              key: "strategiesToGuideBehaviour",
              validation: Yup.string().trim().required("A response is required"),
              props: {
                saveprompt: saveprompt,
              },
            },
          ],
        },
      ],
    },
    {
      type: "field",
      fieldType: "advisory",
      key: "advisory",
      props: {
        type: "warning",
        styleClasses: "mt-5",
        Body: () => (
          <div>
            For each of the domains in the context of the child, you will be asked whether support is required for each domain. If you tick yes – that
            support is required – you will then be asked to describe the assistance.
            <p>
              When you are answering the questions in these sections, please only consider information where the support required is beyond what is
              developmentally age-appropriate for the child.
            </p>
            <p>
              When you are describing supports required, please only describe support that is beyond what would be typically provided in the
              kindergarten setting.
            </p>
            <p>Further prompts about the information to consider in your responses are provided for each question.</p>
          </div>
        ),
      },
    },
    {
      type: "formGroup",
      heading: "Motor skills",
      key: "motorSkills",
      hideExpression: (context) => context.applicationCategory == 0, // HIDE *IF* [Application Category] component, [Application Category] field = [Complex Medical Needs]
      fields: [
        {
          type: "field",
          fieldType: "radio",
          label: "Does the child require assistance to physically move around the kindergarten service?",
          extendedlabel: (
            <div>
              <p>
                This question refers to the child's level of independence in moving around and accessing the kindergarten environment and their gross
                motor skills.
              </p>
              <br />
              <p>
                It is not referring to supervising children with typically developing gross motor skills as they move around the environment due to
                concerns about their safety or the safety of others.
              </p>
              <br />
              <p>If the child has a disability or condition that impacts their movement and requires assistance, yes should be selected.</p>
            </div>
          ),
          key: "doesChildRequireAssistanceToMove",
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
        },
        {
          type: "field",
          fieldType: "radio",
          label: "Does the child use Specialised Equipment?",
          key: "doesChildUseSpecialisedEquipment",
          extendedlabel: (
            <div>
              <p>
                This question refers to the child's level of independence in moving around and accessing the kindergarten environment and their gross
                motor skills.
              </p>
              <br />
              <p>
                It is not referring to supervising children with typically developing gross motor skills as they move around the environment due to
                concerns about their safety or the safety of others.
              </p>
              <br />
              <p>If the child has a disability or condition that impacts their movement and requires assistance, yes should be selected.</p>
            </div>
          ),
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
          hideExpression: (context, values, model) => !model.doesChildRequireAssistanceToMove,
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "Describe what assistance the child requires to move around during the kindergarten program",
          extendedlabel: (
            <div>
              <p>
                Please describe the child's gross motor skills and the support they require to physically move around the kindergarten environment and
                access all areas and activities.
              </p>
              <br />
              <p>
                Please describe any specialised equipment that the child uses in the kindergarten setting to move around the environment and access
                activities. Describe the support that the child requires to use this equipment in the kindergarten setting.
              </p>
              <br />
              <p>
                Do not include information here about needing to supervise the child with typically developing gross motor skills as they move around
                the environment due to concerns about their safety or the safety of others.
              </p>
              <br />
              <p>If the child has a disability or condition that impacts their movement, please explain here.</p>
            </div>
          ),
          key: "describeWhatAssistanceChildRequires",
          validation: Yup.string().trim().required("A response is required"),
          hideExpression: (context, values, model) => !model.doesChildRequireAssistanceToMove,
          props: {
            saveprompt: saveprompt,
          },
        },
        {
          type: "field",
          fieldType: "radio",
          label: "Does the child require assistance with fine motor skills e.g. picking up, manipulating and releasing objects using one's hands?",
          key: "doesChildRequireAssistanceWithFineMotorSkills",
          warn: "When answering this question, please consider what is developmentally age-appropriate. For example, it is quite common for a child in a 3 year old kinder class not to be able to use scissors, or a child in a 4 year old kinder class not to be able to open a lunch box.",
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
        },
        {
          type: "field",
          fieldType: "textarea",
          label:
            "Describe what assistance the child requires for their fine motor skills and when they will require assistance during the kindergarten program",
          extendedlabel: (
            <div>
              <p>
                Please describe the child's fine motor skills and the support they require for activities involving fine hand use at kindergarten.
              </p>{" "}
              <br />
              <p>
                Please describe any specialised equipment that the child uses in the kindergarten setting to support their fine hand use. Describe the
                support that the child requires to use this equipment in the kindergarten setting.
              </p>
            </div>
          ),
          key: "describeWhatAssistanceChildRequiresFineMotorSkills",
          validation: Yup.string().trim().required("A response is required"),
          hideExpression: (context, values, model) => !model.doesChildRequireAssistanceWithFineMotorSkills,
          props: {
            saveprompt: saveprompt,
          },
        },
      ],
    },
    {
      type: "formGroup",
      key: "selfCare",
      heading: "Self-care",
      hideExpression: (context) => context.applicationCategory == 0, //HIDE *IF* [Application Category] component, [Application Category] field = [Complex Medical Needs]
      fields: [
        {
          type: "field",
          fieldType: "radio",
          label: "Does the child require assistance with self-care skills, e.g. toileting, eating?",
          key: "doesChildRequireAssistanceWithSelfCareSkills",
          warn: "When answering this question, please consider what is developmentally age-appropriate. For example, it is quite common for a child in a 3 year old kinder class to not be toilet trained, or a 4 year old child to require assistance to apply sunscreen.",
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "Please describe what assistance the child requires with their self-care skills",
          extendedlabel: (
            <div>
              <p>Please describe the support the child requires to complete age-appropriate self-care tasks.</p>
              <br />
              <p>
                Please describe any specialised equipment that the child uses in the kindergarten setting to assist with self-care tasks. Describe the
                support that the child requires to use this equipment in the kindergarten setting.
              </p>
            </div>
          ),
          key: "describeWhatAssistanceChildRequiresSelfCare",
          validation: Yup.string().trim().required("A response is required"),
          hideExpression: (context, values, model) => !model.doesChildRequireAssistanceWithSelfCareSkills,
          props: {
            saveprompt: saveprompt,
          },
        },
      ],
    },
    {
      type: "formGroup",
      key: "engagingWithOthers",
      heading: "Engaging with others",
      hideExpression: (context) => context.applicationCategory == 0, //HIDE *IF* [Application Category] component, [Application Category] field = [Complex Medical Needs]
      fields: [
        {
          type: "field",
          fieldType: "radio",
          label: "Does the child require assistance with communication?",
          key: "doesChildRequireAssistanceWithCommunication",
          warn: "Consider whether the child requires support to understand messages, express their needs and/or participate in conversation using spoken language, signs, symbols or another augmentative and alternative communication (AAC) system.",
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "Please describe how the child communicates and what assistance they require with their communication",
          extendedlabel: (
            <div>
              <p>
                Please describe the child's communication skills and what support the child requires to express their needs or wants, participate in
                an age-appropriate conversation or understand messages from others.
              </p>{" "}
              <br />
              <p>
                Please describe any AAC systems that the child uses to communicate at kindergarten. This may include low-tech systems such as visuals
                or Key Word Signing or high-tech systems such as Proloquo. Describe the support that the child requires to use the communication
                system in the kindergarten setting.
              </p>
            </div>
          ),
          key: "describeWhatAssistanceChildRequiresCommunication",
          validation: Yup.string().trim().required("A response is required"),
          hideExpression: (context, values, model) => !model.doesChildRequireAssistanceWithCommunication,
          props: {
            saveprompt: saveprompt,
          },
        },
        {
          type: "field",
          fieldType: "radio",
          label: "Does the child require support with their interpersonal interactions?",
          key: "doesChildRequireAssistanceWithInterpersonalInteractions",
          extendedlabel: (
            <div>
              <p>Consider whether the child requires support to interact with peers safely and share space with peers at kindergarten.</p>
              <br />
              <p>
                For example, do they require support to play with peers, respond to peers around them, share resources, take turns at activities or
                demonstrate age-appropriate behaviours when playing with others. Consider if the support required is beyond what would typically be
                provided in the kindergarten setting.
              </p>
            </div>
          ),
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "Please describe what assistance the child requires with interpersonal interactions",
          warn: "Please describe what support the child requires to interact with peers, share space with peers, share resources with others and engage in play with others safely.",
          key: "describeWhatAssistanceChildRequiresInterpersonalInteractions",
          validation: Yup.string().trim().required("A response is required"),
          hideExpression: (context, values, model) => !model.doesChildRequireAssistanceWithInterpersonalInteractions,
          props: {
            saveprompt: saveprompt,
          },
        },
      ],
    },
    {
      type: "formGroup",
      key: "learning",
      heading: "Learning",
      hideExpression: (context) => context.applicationCategory == 0, //HIDE *IF* [Application Category] component, [Application Category] field = [Complex Medical Needs]
      fields: [
        {
          type: "field",
          fieldType: "radio",
          label: "Does the child require additional support to learn and apply knowledge?",
          key: "doesChildRequireAssistanceWithLearningAndApplyingKnowledge",
          warn: "Consider whether the child requires support to focus and direct attention, undertake tasks and problem solve to an age-appropriate level.",
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "Please describe what additional support the child requires with learning during the kindergarten program",
          warn: "Please describe what support the child requires to support them to learn and apply knowledge. This includes support to focus and direct attention, engage and persevere with activities, plan and organise actions to complete a task, remember previous learning and solve problems to an age-appropriate level.",
          key: "describeWhatAssistanceChildRequiresLearningAndApplyingKnowledge",
          validation: Yup.string().trim().required("A response is required"),
          hideExpression: (context, values, model) => !model.doesChildRequireAssistanceWithLearningAndApplyingKnowledge,
          props: {
            saveprompt: saveprompt,
          },
        },
      ],
    },
    {
      type: "formGroup",
      key: "complexNeeds",
      heading: "Complex medical needs",
      hideExpression: (context) => context.applicationCategory == 1, // HIDE *IF* [Application Category] component, [Application Category] field = [Disability (including developmental delay)]
      fields: [
        {
          type: "field",
          fieldType: "textarea",
          label: "Provide the child's medical diagnosis and describe their medical condition",
          key: "childsMedicalDiagnosis",
          validation: Yup.string().trim().required("A response is required"),
          props: {
            saveprompt: saveprompt,
          },
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "What health support procedures are required for this child during the kindergarten program?",
          extendedlabel: (
            <div>
              <p>
                Health support procedures are specific actions and supports that require the ECT or other members of the teaching team to intervene in
                order to meet the child's ongoing medical needs as described in the Medical Management Plan.
              </p>
              <p>
                This includes support to provide regular medication such as insulin or midazolam, support to monitor blood sugar levels or blood
                oxygen saturation levels, support for PEG feeding, or support for stomal care.
              </p>
              <p>Please note that additional supervision is not considered a health support procedure.</p>
            </div>
          ),
          key: "whatSupportProceduresRequired",
          validation: Yup.string().trim().required("A response is required"),
          props: {
            saveprompt: saveprompt,
          },
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "When and how often will the child require this health support during the kindergarten program?",
          warn: "Please describe when during the program the health support is required, how often the support is required and how long it takes to provide the health support.",
          key: "whenWillChildRequireHealthSupport",
          validation: Yup.string().trim().required("A response is required"),
          props: {
            saveprompt: saveprompt,
          },
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "Who is providing or will provide the health support required for this child?",
          warn: "Please describe who is providing or will provide the ongoing health support procedures for this child. Please confirm if they are suitably trained or will be suitably trained to provide this support.",
          key: "whosProvidingHealthSupport",
          validation: Yup.string().trim().required("A response is required"),
          props: {
            saveprompt: saveprompt,
          },
        },
        {
          type: "field",
          fieldType: "radio",
          label: "Have the kindergarten team already undertaken training to support the child's complex medical needs?",
          key: "hasKindergartenTeamUndertakenTraining",
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "Provide details of the training undertaken to support the child's complex medical needs",
          warn: "Please include the name of the training course/s and the date/s conducted.",
          key: "provideDetailsOfTrainingUndertaken",
          validation: Yup.string().trim().required("A response is required"),
          hideExpression: (context, values, model) => !model.hasKindergartenTeamUndertakenTraining,
          props: {
            saveprompt: saveprompt,
          },
        },

        {
          type: "field",
          fieldType: "radio",
          label: "Will it be necessary to adjust any of the usual practices of the kindergarten program in order to be fully inclusive of the child?",
          key: "isNecessaryToAdjustUsualPractices",
          props: {
            options: [
              { label: "Yes", value: true },
              { label: "No", value: false },
            ],
          },
          validation: Yup.boolean().required("A response is required"),
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "What adjustments will be made by the program?",
          key: "whatAdjustmentsWillBeMade",
          validation: Yup.string().trim().required("A response is required"),
          hideExpression: (context, values, model) => model.isNecessaryToAdjustUsualPractices != 1,
          props: {
            saveprompt: saveprompt,
          },
        },
      ],
    },
    {
      type: "formGroup",
      heading: "Other information",
      fields: [
        {
          type: "formGroup",
          key: "existingResources",
          label:
            "What existing resources has your kindergarten service accessed (or will access) to support the early childhood educators to plan an inclusive program for this child? Select all that apply.",

          test: [
            "chooseOne",
            "A response is required",
            (value: any) => {
              return (
                value.family ||
                value.earlyYearsManagementSupport ||
                value.earlyInterventionProfessionalTherapists ||
                value.preschoolFieldOfficer ||
                value.fkaMulticulturalResourceCentre ||
                value.earlyAblesPlanningToolEyalt ||
                value.deOnlineInclusionModules ||
                value.professionalDevelopment ||
                value.existingResourcesOtherPleaseSpecify ||
                value.none
              );
            },
          ],
          props: {
            isRequired: true,
          },
          fields: [
            {
              type: "field",
              key: "family",
              fieldType: "checkbox",
              label: "Family",
            },
            {
              type: "field",
              key: "earlyYearsManagementSupport",
              fieldType: "checkbox",
              label: "Early Years Management support",
            },
            {
              type: "field",
              key: "earlyInterventionProfessionalTherapists",
              fieldType: "checkbox",
              label: "Early intervention professional therapists",
            },
            {
              type: "field",
              key: "preschoolFieldOfficer",
              fieldType: "checkbox",
              label: "Preschool Field Officer",
            },
            {
              type: "field",
              key: "fkaMulticulturalResourceCentre",
              fieldType: "checkbox",
              label: "FKA/Multicultural Resource Centre",
            },
            {
              type: "field",
              key: "earlyAblesPlanningToolEyalt",
              fieldType: "checkbox",
              label: "Early ABLES planning tool/EYALT",
            },
            {
              type: "field",
              key: "deOnlineInclusionModules",
              fieldType: "checkbox",
              label: "DE online inclusion modules",
            },
            {
              type: "field",
              key: "professionalDevelopment",
              fieldType: "checkbox",
              label: "Professional development",
            },
            {
              type: "field",
              key: "existingResourcesOtherPleaseSpecify",
              fieldType: "checkbox",
              label: "Other - please specify",
            },
            {
              type: "field",
              key: "none",
              fieldType: "checkbox",
              label: "None",
              warn: (
                <>
                  For more information about the EYALT, please refer to{" "}
                  <a
                    target="_blank"
                    title="Link opens in a new tab"
                    href="https://www.vic.gov.au/early-years-assessment-and-learning-tool"
                    rel="noreferrer">
                    Early Years Assessment and Learning Tool (EYALT) | vic.gov.au
                  </a>{" "}
                  <i aria-hidden="true" className="pi pi-external-link"></i>
                </>
              ),
            },
          ],
        },
        {
          type: "field",
          fieldType: "textarea",
          label:
            "Please specify the additional existing resources that your kindergarten service has accessed (or will access) to support the early childhood educators to plan an inclusive program for this child",

          key: "pleaseSpecifyAdditionalExistingResources",
          hideExpression: (context, values) => !values.existingResources.existingResourcesOtherPleaseSpecify,
          validation: Yup.string().trim().required("A response is required"),
          props: {
            saveprompt: saveprompt,
          },
        },
        {
          type: "formGroup",
          key: "adviseHowInformationWasGathered",
          label: "Please advise how the information required to complete this section of the application was gathered. Select all that apply.",

          test: [
            "chooseOne",
            "A response is required",
            (value: any) => {
              return (
                value?.fromObservationsAtThisKindergartenService ||
                value?.fromDiscussionsWithTheChildsParentsCarersGuardians ||
                value?.formDiscussionWithTheChildsMedicalAlliedHealthSupportWorkerTeams ||
                value?.fromDiscussionsWithTheServiceTheChildPreviouslyAttended ||
                value?.adviseHowInformationWasGatheredOtherPleaseSpecify
              );
            },
          ],
          props: {
            isRequired: true,
          },
          fields: [
            {
              type: "field",
              key: "fromObservationsAtThisKindergartenService",
              fieldType: "checkbox",
              label: "From observations at this kindergarten service",
            },
            {
              type: "field",
              key: "fromDiscussionsWithTheChildsParentsCarersGuardians",
              fieldType: "checkbox",
              label: "From discussions with the child's parents / carers / guardians",
            },
            {
              type: "field",
              key: "formDiscussionWithTheChildsMedicalAlliedHealthSupportWorkerTeams",
              fieldType: "checkbox",
              label: "Form discussions with the child's medical / allied health / support worker team/s",
            },
            {
              type: "field",
              key: "fromDiscussionsWithTheServiceTheChildPreviouslyAttended",
              fieldType: "checkbox",
              label: "From discussions with the service the child previously attended",
            },
            {
              type: "field",
              key: "adviseHowInformationWasGatheredOtherPleaseSpecify",
              fieldType: "checkbox",
              label: "Other - please specify",
            },
          ],
        },
        {
          type: "field",
          fieldType: "textarea",
          label: "Please specify the other way/s information was gathered",
          key: "pleaseSpecifyOtherWaysInformationWasGathered",
          hideExpression: (context, values) => !values.adviseHowInformationWasGathered.adviseHowInformationWasGatheredOtherPleaseSpecify,
          validation: Yup.string().trim().required("A response is required"),
          props: {
            saveprompt: saveprompt,
          },
        },
        {
          type: "field",
          fieldType: "textarea",
          extendedlabel:
            "Additional information should not include what has already been recorded in this application and should not identify other children in the group",
          label: "Is there any other relevant information you wish to provide?",
          key: "anyOtherRelevantInformation",
          props: {
            saveprompt: saveprompt,
          },
        },
      ],
    },
  ],
};
